import { useState, useRef, useEffect } from 'react';
import { Animated } from 'react-native';
import { supabase } from '../lib/supabase';
import { SUPABASE_URL, SUPABASE_ANON_KEY } from '../lib/constants';
import { HomeEnrichmentData } from '../components/onboarding/types';
import { FormData } from '../components/onboarding/types';

// Progress messages to rotate through during enrichment
const progressMessages = [
  'Validating address',
  'Finding square footage',
  'Counting bedrooms',
  'Counting bathrooms',
  'Checking year built',
  'Searching transactions',
  'Gathering property details',
  'Verifying home information'
];

export const useHomeEnrichment = (
  selectedPlace: any,
  formData: FormData,
  setFormData: React.Dispatch<React.SetStateAction<FormData>>
) => {
  const [enrichingHomeData, setEnrichingHomeData] = useState(false);
  const [enrichmentError, setEnrichmentError] = useState<string | null>(null);
  const [enrichedData, setEnrichedData] = useState<HomeEnrichmentData | null>(null);
  const [createdHomeId, setCreatedHomeId] = useState<string | null>(null);
  const [currentProgressMessage, setCurrentProgressMessage] = useState(0);
  const fadeAnim = useRef(new Animated.Value(1)).current;
  const enrichmentTriggeredRef = useRef<string | null>(null);
  const homeCreationInProgressRef = useRef<boolean>(false);

  // Animate progress messages when enriching
  useEffect(() => {
    if (!enrichingHomeData) {
      setCurrentProgressMessage(0);
      fadeAnim.setValue(1); // Reset opacity when stopping
      return;
    }

    // Start with first message visible
    fadeAnim.setValue(1);

    const interval = setInterval(() => {
      // Fade out
      Animated.timing(fadeAnim, {
        toValue: 0,
        duration: 300,
        useNativeDriver: true,
      }).start(() => {
        // Update message
        setCurrentProgressMessage((prev) => (prev + 1) % progressMessages.length);
        // Fade in
        Animated.timing(fadeAnim, {
          toValue: 1,
          duration: 300,
          useNativeDriver: true,
        }).start();
      });
    }, 2000); // Change message every 2 seconds

    return () => clearInterval(interval);
  }, [enrichingHomeData, fadeAnim]);

  // Create home record immediately when address is selected (before enrichment)
  const createHomeRecord = async (): Promise<string | null> => {
    if (!selectedPlace) return null;
    if (createdHomeId) return createdHomeId; // Already created
    
    // Prevent concurrent creation attempts
    if (homeCreationInProgressRef.current) {
      // Wait a bit and check again
      await new Promise(resolve => setTimeout(resolve, 100));
      if (createdHomeId) return createdHomeId;
      // If still not created, something went wrong, but don't create duplicate
      return null;
    }

    homeCreationInProgressRef.current = true;

    try {
      const { data, error: homeError } = await supabase
        .from('homes')
        .insert({
          owner_id: null,
          address: selectedPlace.street_address,
          city: selectedPlace.city,
          state: selectedPlace.state,
          zip_code: selectedPlace.zip_code,
        })
        .select('id')
        .single();

      if (homeError) {
        console.error('Error creating home record:', homeError);
        return null;
      }
      
      if (data?.id) {
        setCreatedHomeId(data.id);
        return data.id;
      }
      return null;
    } catch (err: any) {
      console.error('Error creating home record:', err);
      return null;
    } finally {
      homeCreationInProgressRef.current = false;
    }
  };

  // Enrich home data when address is selected (waits for home to be created first)
  const enrichHomeData = async (providedHomeId?: string | null) => {
    if (!selectedPlace) return;

    const addressKey = `${selectedPlace.street_address}-${selectedPlace.city}-${selectedPlace.state}`;
    if (enrichmentTriggeredRef.current === addressKey) {
      return; // Already triggered for this address
    }

    enrichmentTriggeredRef.current = addressKey;
    setEnrichingHomeData(true);
    setEnrichmentError(null);
    setEnrichedData(null);

    try {
      // Use provided homeId, or check state, or create if needed
      let homeId = providedHomeId || createdHomeId;
      if (!homeId) {
        // Fallback: create if somehow it doesn't exist (shouldn't happen with proper flow)
        homeId = await createHomeRecord();
        if (!homeId) {
          throw new Error('Failed to create home record');
        }
      }

      // Call edge function without authentication (user not logged in during onboarding)
      // Use anon key as bearer token for public access
      const response = await fetch(`${SUPABASE_URL}/functions/v1/enrich-home-data`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
          'apikey': SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({
          address: selectedPlace.street_address,
          city: selectedPlace.city,
          state: selectedPlace.state,
          zip_code: selectedPlace.zip_code,
          home_id: homeId || undefined, // Pass home_id so edge function can persist
          provider: 'firecrawl',
        }),
      });

      if (!response.ok) {
        await response.text(); // Consume response body
        throw new Error(`Failed to enrich home data: ${response.status}`);
      }

      const result = await response.json();

      if (result.success && result.data) {
        setEnrichedData(result.data);
        // Pre-populate form data with enriched values
        // Note: cover_photo_url is stored in enrichedData but not in formData since it's not user-editable
        // Convert last_sale_date to purchaseDate format (YYYY-MM-DD)
        let purchaseDateValue = '';
        if (result.data.last_sale_date) {
          // last_sale_date comes as ISO date string, format it to YYYY-MM-DD
          try {
            const date = new Date(result.data.last_sale_date);
            if (!isNaN(date.getTime())) {
              purchaseDateValue = date.toISOString().split('T')[0];
            }
          } catch (e) {
            // If parsing fails, use the value as-is if it's already in YYYY-MM-DD format
            purchaseDateValue = result.data.last_sale_date;
          }
        }
        
        setFormData(prev => ({
          ...prev,
          bedrooms: result.data.bedrooms?.toString() || '',
          bathrooms: result.data.bathrooms?.toString() || '',
          squareFootage: result.data.square_footage?.toString() || '',
          yearBuilt: result.data.year_built?.toString() || '',
          purchaseDate: purchaseDateValue,
        }));
        
        // Edge function persists data automatically when home_id is provided
      } else {
        setEnrichmentError(result.error || 'Could not find home details automatically');
      }
    } catch (err: any) {
      console.error('Error enriching home data:', err);
      setEnrichmentError('Failed to look up home details. Please enter them manually.');
    } finally {
      setEnrichingHomeData(false);
    }
  };

  // Update home with user-entered form data when proceeding from step 2
  const updateHomeForOnboarding = async () => {
    if (!selectedPlace) {
      throw new Error('No address selected');
    }

    // Ensure home exists (should already exist from address selection)
    let homeId = createdHomeId;
    if (!homeId) {
      homeId = await createHomeRecord();
      if (!homeId) {
        throw new Error('Failed to create home record. Please try again.');
      }
    }

    // Update home with current form data (user may have modified enriched values)
    try {
      // Parse purchase date - convert YYYY-MM-DD string to date or null
      let purchaseDateValue: string | null = null;
      if (formData.purchaseDate && formData.purchaseDate.trim()) {
        // Validate date format (YYYY-MM-DD)
        const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
        if (dateRegex.test(formData.purchaseDate.trim())) {
          purchaseDateValue = formData.purchaseDate.trim();
        }
      }

      const { error: updateError } = await supabase
        .from('homes')
        .update({
          bedrooms: parseInt(formData.bedrooms) || null,
          bathrooms: parseFloat(formData.bathrooms) || null,
          square_footage: parseInt(formData.squareFootage) || null,
          year_built: parseInt(formData.yearBuilt) || null,
          purchase_date: purchaseDateValue,
          cover_photo_url: enrichedData?.cover_photo_url || null
        })
        .eq('id', homeId);

      if (updateError) {
        console.error('Error updating home:', updateError);
        throw new Error(updateError.message || 'Failed to update home. Please try again.');
      }

      return homeId;
    } catch (err: any) {
      if (err instanceof Error) {
        throw err;
      }
      throw new Error('Failed to update home. Please try again.');
    }
  };

  // Trigger enrichment immediately when address is selected (proactive)
  // This effect creates the home and triggers enrichment in sequence
  useEffect(() => {
    if (selectedPlace) {
      const addressKey = `${selectedPlace.street_address}-${selectedPlace.city}-${selectedPlace.state}`;
      if (enrichmentTriggeredRef.current !== addressKey) {
        // Create home first, then enrich (prevents race condition)
        (async () => {
          // Ensure home exists before enrichment - get the ID directly
          const homeId = createdHomeId || await createHomeRecord();
          if (homeId) {
            // Pass homeId directly to enrichment to avoid state timing issues
            enrichHomeData(homeId);
          }
        })();
      }
    }
  }, [selectedPlace?.street_address, selectedPlace?.city, selectedPlace?.state]);

  const clearEnrichment = () => {
    setCreatedHomeId(null);
    setEnrichedData(null);
    enrichmentTriggeredRef.current = null;
    setEnrichmentError(null);
  };

  return {
    enrichingHomeData,
    enrichmentError,
    enrichedData,
    createdHomeId,
    setCreatedHomeId,
    currentProgressMessage,
    fadeAnim,
    progressMessages,
    createHomeRecord,
    enrichHomeData,
    updateHomeForOnboarding,
    clearEnrichment
  };
};
